/**
 * Authenticated fetch wrapper. All `/api/*` calls from the frontend go
 * through this so the bearer token is consistently attached.
 *
 * The token-getter is injected (not imported from AuthProvider) so this
 * module stays decoupled from the React context — TanStack Query hooks
 * pass it through.
 */

import { resolveAuthMode } from '../auth/authMode';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export class HttpError extends Error {
  constructor(
    public status: number,
    public bodyText: string,
    public errId?: string,
    /** FastAPI `detail.code` when the body parses as JSON with that shape. */
    public detailCode?: string,
    /** FastAPI `detail.message` — the user-presentable string. */
    public detailMessage?: string,
  ) {
    super(`HTTP ${status}${detailCode ? ` ${detailCode}` : errId ? ` ${errId}` : ''}`);
  }
}

const ERR_RE = /ERR_[A-Z0-9]{8}/;

function tryParseDetail(bodyText: string): { code?: string; message?: string } {
  try {
    const parsed = JSON.parse(bodyText) as { detail?: { code?: string; message?: string } };
    return { code: parsed?.detail?.code, message: parsed?.detail?.message };
  // intentional swallow: non-JSON error bodies are expected (HTML 5xx pages,
  // empty bodies); falling through to undefined code/message is the contract.
  } catch {
    return {};
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'headers' | 'body'> {
  body?: unknown;
  getIdToken?: () => Promise<string>;
  headers?: Record<string, string>;
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = { 'content-type': 'application/json', ...(opts.headers ?? {}) };
  if (opts.getIdToken) {
    const token = await opts.getIdToken();
    headers['authorization'] = `Bearer ${token}`;
  }
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    // Fleet (dev) auth IS the OAuth2 Proxy cookie, so it has to be sent; the
    // bearer-token modes keep cookies off the wire as before.
    credentials: opts.credentials ?? (resolveAuthMode() === 'fleet' ? 'same-origin' : 'omit'),
    headers,
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
  }
  const resp = await fetch(url, init);
  const text = await resp.text();
  if (!resp.ok) {
    const errMatch = text.match(ERR_RE);
    const { code, message } = tryParseDetail(text);
    throw new HttpError(resp.status, text, errMatch?.[0], code, message);
  }
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}
